import * as React from 'react';
import { connect } from 'react-redux';

import Spinner from 'components/spinner/Spinner';
import Page from 'components/page/Page';
import SharedAccessError from './shared-access-error/SharedAccessError';

import { resetSharedAccess, sharedAccessPageFetchMeta } from 'modules/shared-access/reducers/sharedAccess';

import { __t, applyTranslations } from 'utils/language';

import type { LocationWindow } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./with-shared-access-authentication.i18n.json'));

type OwnProps = {
  location: LocationWindow
};

type StateProps = {
  isLoading: boolean,
  isAuthenticated: boolean,
  hasInvalidToken: boolean
};

type DispatchProps = {
  dispatchFetchMeta: (b: string, a: string) => Promise<any>,
  dispatchResetSharedAccess: () => Promise<any>
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  invalidRoute: boolean
};

const withSharedAccessAuthentication = () =>
  (ChildComponent: React.ComponentType<any>) => {
    class WrappedComponent extends React.Component<Props, State> {
      state = {
        invalidRoute: false
      };

      componentDidMount(): void {
        const { location: { params, query }, dispatchFetchMeta } = this.props;
        const id = params && params.id;
        const token = query && query.token;

        if (token && id) {
          dispatchFetchMeta(id, token);
        } else {
          this.setState({ invalidRoute: true });
        }
      }

      componentWillUnmount(): void {
        const { dispatchResetSharedAccess } = this.props;

        dispatchResetSharedAccess();
      }

      renderError(title: string, message: string): React.ReactNode {
        return (
          <Page
            documentTitle={title}
          >
            <SharedAccessError
              title={title}
              message={message}
            />
          </Page>
        );
      }

      render(): React.ReactNode {
        const { isLoading, hasInvalidToken, isAuthenticated } = this.props;
        const { invalidRoute } = this.state;

        if (invalidRoute) {
          return this.renderError(
            __t('with_shared_access_authentication.invalid_url.title'),
            __t('with_shared_access_authentication.invalid_url.message')
          );
        }
        if (isLoading) {
          return <Spinner position="cover" />;
        }
        if (hasInvalidToken) {
          return this.renderError(
            __t('with_shared_access_authentication.expired_url.title'),
            __t('with_shared_access_authentication.expired_url.message')
          );
        }
        if (isAuthenticated) {
          return <ChildComponent {...this.props} />;
        }
        return null;
      }
    }

    const mapStateToProps = ({ sharedAccess: { isAuthenticated, isLoading, hasInvalidToken } }): StateProps => ({
      isAuthenticated,
      isLoading,
      hasInvalidToken
    });

    const mapDispatchToProps = (dispatch): DispatchProps => ({
      dispatchFetchMeta: (id, token) => dispatch(sharedAccessPageFetchMeta(id, token)),
      dispatchResetSharedAccess: () => dispatch(resetSharedAccess())
    });

    return connect(mapStateToProps, mapDispatchToProps)(WrappedComponent);
  };

export default withSharedAccessAuthentication;
